import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowDown, MessageCircle, Flame, Timer, ChevronLeft, ChevronRight } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";
import { HeroNewLaunch } from "./HeroNewLaunch";

const HERO_IMG = "/hero-sublimation.jpg";
const SLIDE_MS = 6000;

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { delay: 0.25 + i * 0.14, duration: 0.9, ease: [0.16, 1, 0.3, 1] },
  }),
};

function MaskedLine({ index, children, className = "" }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        custom={index}
        variants={lineVariants}
        initial="hidden"
        animate="visible"
        className={`block ${className}`}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const { setDrawerOpen } = useCart();
  const [settings, setSettings] = useState({});
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  const slides = settings.hero_slides?.length
    ? settings.hero_slides
    : [{ url: settings.hero_image || HERO_IMG, type: "image" }];
  const current = slides[Math.min(slide, slides.length - 1)] || slides[0];

  useEffect(() => {
    if (slides.length < 2 || current?.type === "video") return undefined;
    const t = setTimeout(() => setSlide((i) => (i + 1) % slides.length), SLIDE_MS);
    return () => clearTimeout(t);
  }, [slide, slides.length, current?.type]);

  const go = (dir) => setSlide((i) => (i + dir + slides.length) % slides.length);

  return (
    <section id="top" data-testid="hero-section" className="relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(160deg,#0E1016_0%,#0A0B0E_60%,#101118_100%)]" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-20 lg:pt-14 lg:pb-28">
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.15, duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="relative mb-14 lg:mb-20"
        >
          <div
            data-testid="hero-tilt-card"
            className="spotlight-frame relative aspect-[16/9] w-full overflow-hidden rounded-3xl bg-[#12141A]"
          >
            <AnimatePresence mode="popLayout">
              <motion.div
                key={current.url + slide}
                initial={{ opacity: 0, scale: 1.04 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className="absolute inset-0"
              >
                {current.type === "video" ? (
                  <video
                    data-testid="hero-slide-video"
                    src={current.url || HERO_IMG}
                    autoPlay
                    muted
                    playsInline
                    onEnded={() => slides.length > 1 && go(1)}
                    style={{ objectPosition: `center ${current.focus ?? 50}%` }}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <img
                    data-testid="hero-slide-image"
                    src={current.url || HERO_IMG}
                    alt="Sublimation printing at Varsha Sublimation"
                    onError={(e) => { e.currentTarget.src = HERO_IMG; }}
                    style={{ objectPosition: `center ${current.focus ?? 50}%` }}
                    className="h-full w-full object-cover"
                  />
                )}
              </motion.div>
            </AnimatePresence>
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0A0B0E] via-transparent to-transparent" />
            {slides.length > 1 && (
              <>
                <button
                  type="button"
                  data-testid="hero-slide-prev"
                  onClick={() => go(-1)}
                  className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-[#0A0B0E]/60 text-slate-200 backdrop-blur transition-colors hover:text-cyan-300 sm:left-5"
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  type="button"
                  data-testid="hero-slide-next"
                  onClick={() => go(1)}
                  className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-[#0A0B0E]/60 text-slate-200 backdrop-blur transition-colors hover:text-cyan-300 sm:right-5"
                >
                  <ChevronRight size={18} />
                </button>
                <div data-testid="hero-slide-dots" className="absolute bottom-16 left-1/2 flex -translate-x-1/2 gap-2 sm:bottom-20">
                  {slides.map((s, i) => (
                    <button
                      key={s.url + i}
                      type="button"
                      data-testid={`hero-slide-dot-${i}`}
                      onClick={() => setSlide(i)}
                      className={`h-1.5 rounded-full transition-all duration-300 ${i === slide ? "w-7 bg-cyan-300" : "w-3 bg-white/35 hover:bg-white/60"}`}
                    />
                  ))}
                </div>
              </>
            )}
            <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between gap-2 p-3 sm:p-7">
              <div data-testid="hero-caption" className="truncate font-mono text-[10px] uppercase tracking-[0.15em] text-cyan-300 sm:text-xs sm:tracking-[0.2em]">{settings.hero_caption || "Sublimation in Action"}</div>
              <div className="flex gap-2">
                {settings.hero_badge_1 && (
                  <span data-testid="hero-badge-1" className="flex items-center gap-1 rounded-full bg-white/10 px-3 py-1.5 font-mono text-xs text-yellow-300 backdrop-blur">
                    <Flame size={12} /> {settings.hero_badge_1}
                  </span>
                )}
                {settings.hero_badge_2 && (
                  <span data-testid="hero-badge-2" className="flex items-center gap-1 rounded-full bg-white/10 px-3 py-1.5 font-mono text-xs text-fuchsia-300 backdrop-blur">
                    <Timer size={12} /> {settings.hero_badge_2}
                  </span>
                )}
              </div>
            </div>
            <div data-testid="hero-offer-card" className="absolute right-3 top-3 rounded-xl border border-white/10 bg-[#181B24]/90 px-3 py-2 backdrop-blur-xl sm:right-7 sm:top-8 sm:rounded-2xl sm:px-4 sm:py-3">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{settings.hero_offer_title || "Full Colour"}</div>
              <div className="font-display text-sm font-bold text-cyan-300 sm:text-lg">
                {settings.hero_offer_text || <>CMYK <span className="text-xs text-slate-500">/ photo-real</span></>}
              </div>
            </div>
          </div>
        </motion.div>
        <div className="grid items-center gap-12 lg:grid-cols-[1.5fr_1fr] lg:gap-16">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-mono text-xs uppercase tracking-[0.25em] text-cyan-400 mb-6"
            data-testid="hero-eyebrow"
          >
            Varsha Sublimation — Manufacturer & Importer Since 2010
          </motion.p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.02]" data-testid="hero-headline">
            <MaskedLine index={0}>EVERYTHING FOR</MaskedLine>
            <MaskedLine index={1}>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-fuchsia-500 to-yellow-300">SUBLIMATION</span>
            </MaskedLine>
            <MaskedLine index={2} className="text-outline">PRINTING</MaskedLine>
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href="#catalog"
              data-testid="hero-explore-catalog-btn"
              className="group flex h-14 w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-cyan-400 to-fuchsia-500 px-7 text-sm font-bold sm:h-auto sm:w-auto sm:py-3.5 text-[#0A0B0E] transition-transform duration-300 hover:scale-105 active:scale-95"
            >
              Explore Full Catalogue
              <ArrowDown size={16} className="transition-transform duration-300 group-hover:translate-y-1" />
            </a>
            <button
              data-testid="hero-build-order-btn"
              onClick={() => setDrawerOpen(true)}
              className="flex h-14 w-full items-center justify-center gap-2 rounded-full border border-cyan-400/40 px-7 text-sm font-semibold sm:h-auto sm:w-auto sm:py-3.5 text-cyan-300 transition-colors duration-300 hover:bg-cyan-400/10 hover:border-cyan-400"
            >
              <MessageCircle size={16} />
              Build WhatsApp Order
            </button>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="mt-12 flex gap-8 font-mono text-xs text-slate-500 sm:gap-10"
          >
            {[["1750+", "Products"], ["2010", "Est. Since"], ["48h", "Dispatch"]].map(([v, l]) => (
              <div key={l}>
                <div className="text-2xl font-bold text-slate-100">{v}</div>
                <div className="mt-1 uppercase tracking-[0.2em]">{l}</div>
              </div>
            ))}
          </motion.div>
        </div>
          <HeroNewLaunch />
        </div>
      </div>
    </section>
  );
}
